/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

import { cloneDeep } from 'lodash';
import moment from 'moment';
import * as ELD_CONSTANTS from '../../constants/ELDConstants';
import { PC_YM_STATUS } from 'src/features/hosLogs/hosLog/types';
import { TIME_MILLISECONDS_FORMAT } from 'src/utils/dateUtils';

export const EDGE_HOURS = {
  MIDNIGHT: { label: 'M', value: 0 },
  NOON: { label: 'N', value: 12 },
};

export const HOURS_IN_DAY = 24;
export const MILLISECONDS_IN_DAY = 1000 * 60 * 60 * HOURS_IN_DAY;

export const getHoursAndMinutes = statusTime => {
  let displayHours;
  let displayMinutes;
  displayHours = Number(statusTime.asHours());
  displayHours = Math.floor(displayHours);

  if (displayHours < 10) {
    displayHours = '0' + displayHours;
  }
  displayMinutes = statusTime.minutes();
  if (displayMinutes < 10) {
    displayMinutes = '0' + displayMinutes;
  }
  return displayHours + ':' + displayMinutes;
};

export function computeStatusGrid(contents, isSameDayLog, timeZone) {
  if (!contents.hosGridObject || !contents.hosGridObject?.length) {
    return {};
  }

  const { hosGridObject, logLengthHours, logStartTimeISO, logEndTimeISO, statusTimes } = contents;

  const gridData = cloneDeep(hosGridObject);
  const isNoonToNoon = isNoonToNoonLog(contents);
  const statusGrid = [];

  let accumulatedWidth = 0;
  let accumulatedMS = 0;

  const total = Number.isInteger(logLengthHours)
    ? moment.duration(logLengthHours, 'hours').asMilliseconds()
    : MILLISECONDS_IN_DAY;

  for (let i = 0; i < gridData.length; i++) {
    const event = gridData[i];
    if (i + 1 < gridData.length) {
      const nextEvent = gridData[i + 1];

      // Calculate the width as number of ms
      const nextTime = nextEvent.time;
      const normalizedTime = nextTime.substring(0, nextTime.length - 4).trim();
      const widthAsMS = isNoonToNoon
        ? event.duration.asMilliseconds()
        : moment.duration(normalizedTime).asMilliseconds() - accumulatedMS;
      const width = widthAsMS / total;

      statusGrid.push({
        state: ELD_CONSTANTS.ELD_DRIVER_STATUS_REVERSE[event.status],
        offset: accumulatedWidth * 100,
        width: width * 100,
      });

      accumulatedWidth += width;
      accumulatedMS += widthAsMS;
    } else {
      // Calculate the width of the final status
      let finalWidth = (total - accumulatedMS) / total;
      // Adjust width if log is on same day
      const isSameDay = isNoonToNoon
        ? moment().isBetween(moment(logStartTimeISO), moment(logEndTimeISO))
        : isSameDayLog;
      if (isSameDay) {
        const msPassedInDay = isNoonToNoon
          ? moment.duration(
              moment()
                .tz(timeZone)
                .diff(moment(logStartTimeISO).tz(timeZone)),
            )
          : moment.duration(
              moment()
                .tz(timeZone)
                .format(TIME_MILLISECONDS_FORMAT),
            );

        finalWidth = (msPassedInDay - accumulatedMS) / total;
      }

      statusGrid.push({
        state: ELD_CONSTANTS.ELD_DRIVER_STATUS_REVERSE[event.status],
        offset: accumulatedWidth * 100,
        width: finalWidth * 100,
      });
    }
  }

  // Off Duty
  let offDutyTime = statusTimes[ELD_CONSTANTS.ELD_DRIVER_STATUS.OFF];
  offDutyTime = getHoursAndMinutes(offDutyTime);

  // SB
  let sbTime = statusTimes[ELD_CONSTANTS.ELD_DRIVER_STATUS.SB];
  sbTime = getHoursAndMinutes(sbTime);

  // D
  let driveTime = statusTimes[ELD_CONSTANTS.ELD_DRIVER_STATUS.D];
  driveTime = getHoursAndMinutes(driveTime);

  // ON DUTY TIME
  let onDutyTime = statusTimes[ELD_CONSTANTS.ELD_DRIVER_STATUS.ON];
  onDutyTime = getHoursAndMinutes(onDutyTime);

  return {
    statusGrid,
    statusTimes: {
      off: offDutyTime,
      sb: sbTime,
      d: driveTime,
      on: onDutyTime,
    },
    violationsObject: contents.violationsObject,
  };
}

const ELD_DRIVER_STATUS = {
  OFF: 1,
  SB: 2,
  D: 3,
  ON: 4,
  OFF_PC: 5,
  ON_YM: 6,
};

const DATE_FORMAT = 'yyyy-MM-DD';

export const calculateStatusTimes = function({ logDate, log, options = {} }) {
  const { hosGridObject: hosGridObj, timeZone, logStartTimeISO, logEndTimeISO } = log;
  const isNoonToNoon = isNoonToNoonLog(log);

  const statusTimes = {
    [ELD_DRIVER_STATUS.OFF]: moment.duration(0),
    [ELD_DRIVER_STATUS.SB]: moment.duration(0),
    [ELD_DRIVER_STATUS.D]: moment.duration(0),
    [ELD_DRIVER_STATUS.ON]: moment.duration(0),
  };

  const totalDuration = moment.duration(0);
  for (let i = 0; i < hosGridObj.length; i++) {
    const event = hosGridObj[i];
    let eventCode = event.status;
    const eventTime = event.time;
    const normalizedTime = eventTime.substring(0, eventTime.length - 4).trim();
    const durationWithoutTimeISO = moment.duration(normalizedTime);
    totalDuration.add(durationWithoutTimeISO.subtract(totalDuration));

    // Adjust status if PC or YM
    if (eventCode === ELD_DRIVER_STATUS.OFF_PC) {
      eventCode = ELD_DRIVER_STATUS.OFF;
      hosGridObj.pcymStatus = PC_YM_STATUS.PC;
    } else if (eventCode === ELD_DRIVER_STATUS.ON_YM) {
      eventCode = ELD_DRIVER_STATUS.ON;
      hosGridObj.pcymStatus = PC_YM_STATUS.YM;
    }

    if (i + 1 < hosGridObj.length) {
      const nextEvent = hosGridObj[i + 1];
      const eventTime = isNoonToNoon ? event.timeISO : event.time;
      const nextEventTime = isNoonToNoon ? nextEvent.timeISO : nextEvent.time;
      const normalizedTime = nextEventTime.substring(0, nextEventTime.length - 4).trim();
      const eventDuration = isNoonToNoon
        ? moment.duration(moment(nextEventTime).diff(moment(eventTime)))
        : moment.duration(normalizedTime).subtract(totalDuration);
      hosGridObj[i].duration = eventDuration;
      statusTimes[eventCode].add(eventDuration);
    } else {
      const date = moment(logDate, DATE_FORMAT);
      const isSame = isNoonToNoon
        ? moment().isBetween(moment(logStartTimeISO), moment(logEndTimeISO))
        : date.isSame(moment(), 'day');
      if (isSame) {
        const finalDuration = isNoonToNoon
          ? moment.duration(
              moment()
                .tz(timeZone)
                .diff(moment(hosGridObj[i].timeISO)),
            )
          : moment
              .duration(
                moment()
                  .tz(timeZone)
                  .format(TIME_MILLISECONDS_FORMAT),
              )
              .subtract(totalDuration);
        hosGridObj[i].duration = finalDuration;
        statusTimes[eventCode].add(finalDuration);
      } else {
        const finalDuration = isNoonToNoon
          ? moment.duration(moment(logEndTimeISO).diff(event.timeISO))
          : moment.duration(HOURS_IN_DAY, 'hours').subtract(totalDuration);
        hosGridObj[i].duration = finalDuration;
        statusTimes[eventCode].add(finalDuration);
      }
    }
  }

  return options.returnAsObject ? hosGridObj : statusTimes;
};

export const hourLabels = ({ timeZone, logStartTimeISO, logLengthHours, isMobile }) => {
  const labels = [];
  const lengthHours = logLengthHours || HOURS_IN_DAY;
  const incrementor = (logLengthHours && logLengthHours > HOURS_IN_DAY) || isMobile ? 2 : 1;
  for (let i = 0; i <= lengthHours; i += incrementor) {
    const hourOfDay = logStartTimeISO
      ? moment
          .tz(logStartTimeISO, timeZone)
          .add(i, 'hours')
          .get('hours')
      : moment
          .tz()
          .startOf('day')
          .add(i, 'hours')
          .get('hours');
    switch (hourOfDay) {
      case EDGE_HOURS.MIDNIGHT.value: {
        labels.push(EDGE_HOURS.MIDNIGHT.label);
        break;
      }
      case EDGE_HOURS.NOON.value: {
        labels.push(EDGE_HOURS.NOON.label);
        break;
      }
      default: {
        labels.push(hourOfDay.toString());
      }
    }
  }
  return labels;
};

export const supportTimeISO = hosGridObject => {
  return hosGridObject.some(event => Boolean(event.timeISO));
};

export const isNoonToNoonLog = ({
  hosGridObject,
  logDayStartTime,
  logStartTimeISO,
  logEndTimeISO,
  logLengthHours,
}) => {
  const hasTimeISO = supportTimeISO(hosGridObject);
  if (!logStartTimeISO || !logEndTimeISO || !logLengthHours) return false;
  return (
    hasTimeISO &&
    (logDayStartTime !== 0 || (logDayStartTime === 0 && logLengthHours > HOURS_IN_DAY))
  );
};
